import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import {
  PackageSearch, CalendarClock, ClipboardList, AlertTriangle, Mail, Plus, X,
  Save, Send, Trash2, Loader2, Info, Bell,
} from 'lucide-react';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']; // index = Python weekday()

const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

// ---- Reusable email-pill editor ----
const EmailPills = ({ label, hint, emails, onChange, testid }) => {
  const [value, setValue] = useState('');
  const add = () => {
    const t = value.trim().toLowerCase();
    if (!t) return;
    if (!EMAIL_RE.test(t)) { toast.error('Enter a valid email address'); return; }
    if (emails.includes(t)) { toast.error('That email is already in the list'); return; }
    if (emails.length >= 10) { toast.error('Maximum 10 recipients'); return; }
    onChange([...emails, t]);
    setValue('');
  };
  return (
    <div>
      <Label>{label}</Label>
      {hint && <p className="text-xs text-gray-500 mt-1 mb-2">{hint}</p>}
      <div className="flex gap-2">
        <Input type="email" placeholder="name@company.com" value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          data-testid={`${testid}-input`} />
        <Button type="button" variant="outline" onClick={add} data-testid={`${testid}-add`}>
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
      {emails.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid={`${testid}-list`}>
          {emails.map((email) => (
            <Badge key={email} variant="secondary" className="text-sm py-1 pl-3 pr-2 gap-1">
              {email}
              <button type="button" onClick={() => onChange(emails.filter((e) => e !== email))}
                className="ml-1 hover:bg-gray-300 rounded-full p-0.5" aria-label={`Remove ${email}`}
                data-testid={`${testid}-remove-${email}`}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

const DEFAULTS = {
  count_reminder: { enabled: false, weekdays: [0], time_of_day: '08:00', staff_recipients: [] },
  change_digest: { enabled: false, time_of_day: '08:00', admin_recipients: [], cc_emails: [] },
  low_stock_alert: { enabled: false, threshold_mode: 'below_par', delta_threshold: null, admin_recipients: [] },
};

const InventoryNotifications = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyJob, setBusyJob] = useState(null);
  const [serverState, setServerState] = useState(null);
  const [form, setForm] = useState(DEFAULTS);

  const hydrate = (data) => ({
    count_reminder: { ...DEFAULTS.count_reminder, ...data.count_reminder },
    change_digest: { ...DEFAULTS.change_digest, ...data.change_digest },
    low_stock_alert: { ...DEFAULTS.low_stock_alert, ...data.low_stock_alert },
  });

  const load = async () => {
    setLoading(true); setLoadError(false);
    try {
      const data = await adminService.getInventoryNotificationSettings();
      setServerState(data);
      setForm(hydrate(data));
    } catch {
      setLoadError(true);
      toast.error('Failed to load inventory notification settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = useMemo(() => {
    if (!serverState) return false;
    return JSON.stringify(form) !== JSON.stringify(hydrate(serverState));
  }, [form, serverState]);

  const setBlock = (block, patch) =>
    setForm((f) => ({ ...f, [block]: { ...f[block], ...patch } }));

  const toggleWeekday = (idx) => {
    const cur = form.count_reminder.weekdays;
    const next = cur.includes(idx) ? cur.filter((d) => d !== idx) : [...cur, idx].sort((a, b) => a - b);
    setBlock('count_reminder', { weekdays: next });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        count_reminder: form.count_reminder,
        change_digest: form.change_digest,
        low_stock_alert: {
          ...form.low_stock_alert,
          delta_threshold: form.low_stock_alert.threshold_mode === 'delta_over'
            ? Number(form.low_stock_alert.delta_threshold) || null
            : null,
        },
      };
      const data = await adminService.updateInventoryNotificationSettings(payload);
      setServerState(data);
      setForm(hydrate(data));
      toast.success('Inventory notification settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => { if (serverState) setForm(hydrate(serverState)); };

  const runJob = async (job) => {
    setBusyJob(job);
    try {
      if (job === 'count') {
        const r = await adminService.triggerInventoryCountReminders();
        toast.success(r.fired ? `Count reminder sent to ${r.bells_sent} staff.` : 'Not a configured weekday — nothing sent.');
      } else {
        const r = await adminService.triggerInventoryChangeDigest();
        toast.success(r.sent ? `Digest sent (${r.changes_count} change(s)).` : `No changes — nothing sent.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to run job');
    } finally {
      setBusyJob(null);
    }
  };

  const countPreview = useMemo(() => {
    const days = form.count_reminder.weekdays.map((d) => WEEKDAYS[d]).join(', ') || 'no days selected';
    return `Bakers will be reminded on ${days} (sent in the daily ${form.count_reminder.time_of_day} batch).`;
  }, [form.count_reminder.weekdays, form.count_reminder.time_of_day]);

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  if (loadError && !serverState) {
    return (
      <div className="space-y-6" data-testid="inventory-notifications-page">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <PackageSearch className="h-6 w-6 text-amber-600" /> Inventory Notifications
        </h2>
        <Card><CardContent className="pt-6">
          <div className="flex items-start gap-3 p-4 border border-red-200 bg-red-50 rounded-lg" data-testid="inventory-load-error">
            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-red-900">Couldn't load settings</p>
              <p className="text-sm text-red-800 mt-1">Your session may have expired or the network glitched.</p>
            </div>
            <Button onClick={load} variant="outline" className="border-red-300" data-testid="inventory-retry-load-btn">Retry</Button>
          </div>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-notifications-page">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <PackageSearch className="h-6 w-6 text-amber-600" /> Inventory Notifications
        </h2>
        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
          Three independent alerts: remind bakers to count, send admins a daily change digest, and
          flag low stock. Each is enabled separately and is unrelated to your general notification settings.
        </p>
      </div>

      {/* ---- Card 1: Count Reminder ---- */}
      <Card data-testid="count-reminder-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><ClipboardList className="h-5 w-5 text-amber-600" /> Count Reminder</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between p-4 bg-gray-50 border rounded-lg">
            <div>
              <Label className="font-semibold">Remind bakers to do the inventory count</Label>
              <p className="text-xs text-gray-500 mt-1">Sent to Kitchen staff (in-app + email) on the selected days.</p>
            </div>
            <Switch checked={form.count_reminder.enabled}
              onCheckedChange={(v) => setBlock('count_reminder', { enabled: v })} data-testid="count-enabled-toggle" />
          </div>
          <div>
            <Label>Days</Label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="count-weekday-picker">
              {WEEKDAYS.map((w, idx) => {
                const active = form.count_reminder.weekdays.includes(idx);
                return (
                  <button key={w} type="button" onClick={() => toggleWeekday(idx)} data-testid={`count-weekday-${idx}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-amber-50'}`}>
                    {w}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="max-w-[180px]">
            <Label htmlFor="count-time">Time of day</Label>
            <Input id="count-time" type="time" value={form.count_reminder.time_of_day}
              onChange={(e) => setBlock('count_reminder', { time_of_day: e.target.value })} data-testid="count-time-input" />
          </div>
          <EmailPills label="Staff recipients" hint="Leave empty to notify all Kitchen staff."
            emails={form.count_reminder.staff_recipients}
            onChange={(v) => setBlock('count_reminder', { staff_recipients: v })} testid="count-recipients" />
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="count-preview">
            <Info className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900">{countPreview}</p>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => runJob('count')} disabled={!serverState?.count_reminder?.enabled || busyJob || isDirty}
              title={isDirty ? 'Save changes first' : ''} data-testid="count-run-now">
              {busyJob === 'count' ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</> : <><Send className="h-4 w-4 mr-2" /> Run Now</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ---- Card 2: Change Digest ---- */}
      <Card data-testid="change-digest-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><CalendarClock className="h-5 w-5 text-amber-600" /> Change Digest</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between p-4 bg-gray-50 border rounded-lg">
            <div>
              <Label className="font-semibold">Daily inventory change digest to admins</Label>
              <p className="text-xs text-gray-500 mt-1">Summarizes the day's quantity & par changes. Sends nothing on a no-change day.</p>
            </div>
            <Switch checked={form.change_digest.enabled}
              onCheckedChange={(v) => setBlock('change_digest', { enabled: v })} data-testid="digest-enabled-toggle" />
          </div>
          <div className="max-w-[180px]">
            <Label htmlFor="digest-time">Time of day</Label>
            <Input id="digest-time" type="time" value={form.change_digest.time_of_day}
              onChange={(e) => setBlock('change_digest', { time_of_day: e.target.value })} data-testid="digest-time-input" />
          </div>
          <EmailPills label="Admin recipients" hint="Leave empty to notify all admins."
            emails={form.change_digest.admin_recipients}
            onChange={(v) => setBlock('change_digest', { admin_recipients: v })} testid="digest-recipients" />
          <EmailPills label="CC emails" hint="Optional extra CC addresses (e.g. operations lead)."
            emails={form.change_digest.cc_emails}
            onChange={(v) => setBlock('change_digest', { cc_emails: v })} testid="digest-cc" />
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => runJob('digest')} disabled={!serverState?.change_digest?.enabled || busyJob || isDirty}
              title={isDirty ? 'Save changes first' : ''} data-testid="digest-run-now">
              {busyJob === 'digest' ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</> : <><Send className="h-4 w-4 mr-2" /> Run Now</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ---- Card 3: Low-Stock Alert ---- */}
      <Card data-testid="low-stock-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Bell className="h-5 w-5 text-amber-600" /> Low-Stock Alert</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between p-4 bg-gray-50 border rounded-lg">
            <div>
              <Label className="font-semibold">Alert admins when stock runs low</Label>
              <p className="text-xs text-gray-500 mt-1">Fires the moment a write crosses the threshold. Deduped once per product per day. Off by default.</p>
            </div>
            <Switch checked={form.low_stock_alert.enabled}
              onCheckedChange={(v) => setBlock('low_stock_alert', { enabled: v })} data-testid="lowstock-enabled-toggle" />
          </div>
          <div>
            <Label>Threshold</Label>
            <RadioGroup value={form.low_stock_alert.threshold_mode}
              onValueChange={(v) => setBlock('low_stock_alert', { threshold_mode: v })}
              className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${form.low_stock_alert.threshold_mode === 'below_par' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}>
                <RadioGroupItem value="below_par" className="mt-1" data-testid="lowstock-mode-below-par" />
                <div><p className="text-sm font-medium">At or below par</p><p className="text-xs text-gray-500">Alert when on-hand ≤ the item's par level.</p></div>
              </label>
              <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${form.low_stock_alert.threshold_mode === 'delta_over' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}>
                <RadioGroupItem value="delta_over" className="mt-1" data-testid="lowstock-mode-delta" />
                <div><p className="text-sm font-medium">N units below par</p><p className="text-xs text-gray-500">Alert only once it drops well past par.</p></div>
              </label>
            </RadioGroup>
          </div>
          {form.low_stock_alert.threshold_mode === 'delta_over' && (
            <div className="max-w-[220px]">
              <Label htmlFor="delta-threshold">Units below par</Label>
              <Input id="delta-threshold" type="number" min="1" step="1"
                value={form.low_stock_alert.delta_threshold ?? ''}
                onChange={(e) => setBlock('low_stock_alert', { delta_threshold: e.target.value })}
                placeholder="e.g. 5" data-testid="lowstock-delta-input" />
            </div>
          )}
          <EmailPills label="Admin recipients" hint="Leave empty to notify all admins."
            emails={form.low_stock_alert.admin_recipients}
            onChange={(v) => setBlock('low_stock_alert', { admin_recipients: v })} testid="lowstock-recipients" />
        </CardContent>
      </Card>

      {/* ---- Save bar ---- */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              {serverState?.updated_at && (
                <div className="flex items-center gap-2 text-xs text-gray-500" data-testid="inventory-notif-meta">
                  <Mail className="h-3.5 w-3.5" /> Last updated {formatDateTime(serverState.updated_at)}
                  {serverState.updated_by_email && <Badge variant="outline" className="ml-1">{serverState.updated_by_email}</Badge>}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleRevert} disabled={!isDirty || saving} data-testid="inventory-notif-revert">
                <Trash2 className="h-4 w-4 mr-2" /> Revert
              </Button>
              <Button onClick={handleSave} disabled={saving || !isDirty} className="bg-amber-600 hover:bg-amber-700" data-testid="inventory-notif-save">
                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-2" /> Save Changes</>}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InventoryNotifications;
